import { type ComponentType, type Dispatch, type ReactElement, type SetStateAction } from 'react';

import { type VirtualItem } from '@tanstack/react-virtual';

import { type Group as GroupType, type Member as MemberType } from '@type/members';

export type MemberRecord = Record<string, MemberType>;
export type GroupRecord = Record<string, GroupType>;

export enum RowType {
    AUTO_ADDED = 'auto-added',
    GROUP = 'group',
    MEMBER = 'member',
    MINUTES_VIEWERS = 'minutes-viewers'
}

export enum AccessManagementPermission {
    EDIT = 'edit',
    MIXED = 'mixed',
    VIEW = 'view',
    VIEW_MINUTES = 'view_minutes'
}

export type ColumnType = {
    className?: string;
    component: any;
    flex?: string;
    justifyContent?: string;
    insertAtIndex?: number;
    title?: any;
    style?: { [key: string]: string };
};

export type AdditionalColumnType = ColumnType & { insertAtIndex: number };

type ScrollToIdData = {
    id: string;
    timestamp: number;
};

export type RowRendererComponentProps = {
    row: AccessRowType;
    columns: ColumnType[];
    className?: string;
    virtualRow: VirtualItem & { key: number };
};

export type AccessManagementProps = {
    accessDropdownOptions:
        | Array<{
              key: string;
              label: string;
          }>
        | ((row: AccessRowType) => Array<{
              key: string;
              label: string;
          }>);
    additionalColumns?: AdditionalColumnType[];
    allowRemoveLoggedMember?: boolean;
    autoAddedInfoMessage?: string;
    dispatch: (action: any) => void;
    dropdownRender?: ({
        searchString,
        setIsDropdownOpen
    }: {
        searchString: string;
        setIsDropdownOpen?: Dispatch<SetStateAction<boolean>>;
    }) => ReactElement;
    emptyRowsComponent?: () => ReactElement;
    groupsById: GroupRecord;
    handleDismissSnackbar?: () => void;
    handleRemoveGroup?: (row: AccessRowType) => void;
    handleRemoveMember?: (row: AccessRowType, keyedRows: KeyedAccessRowRecord) => void;
    headerClassName?: string;
    height?: number;
    id?: string;
    isLoading?: boolean;
    includeMemberTitles?: boolean;
    includeNameColumnTitle?: boolean;
    isLockedEntity?: boolean;
    isSpecialCaseSortingEnabled?: boolean;
    keyedRows: KeyedAccessRowRecord;
    listClassName?: string;
    loggedMemberId?: string;
    manageableGroupsById: GroupRecord;
    manageableMembersById: MemberRecord;
    membersById: MemberRecord;
    noResultsFoundComponent?: ({ searchString }: { searchString: string }) => React.JSX.Element;
    permissionBasedAccessSelectionEnabled?: boolean;
    readOnly?: boolean;
    rowRenderer?: ComponentType<RowRendererComponentProps>;
    searchPlaceholder?: string;
    scrollToId?: ScrollToIdData;
    setScrollToId?: Dispatch<SetStateAction<ScrollToIdData>>;
    showAlreadyAdded?: boolean;
    showRemoveButton?: boolean;
    insights?: {
        addPersonOrGroup?: {
            field?: string;
            name: string;
        };
        adjustPermission?: {
            name: string;
            wootricEvent: string;
        };
    };
};

export type AccessRowType = {
    expanded?: boolean;
    groups?: string[];
    hideDelete?: boolean;
    id: string;
    isAutoAdded?: boolean;
    isDocumentOwner?: boolean;
    isGuest?: boolean;
    isMeetingLeader?: boolean;
    isMeetingOwner?: boolean;
    isInactive?: boolean;
    isParticipant?: boolean;
    isPermissionUpdateDisabled?: boolean;
    isHidden?: boolean;
    members?: string[];
    mixedPermissions?: boolean;
    nested: boolean;
    permission: AccessManagementPermission | null;
    permissions?: { [key: string]: boolean };
    type: RowType;
};

export type KeyedAccessRowRecord = Record<string, AccessRowType>;

export type ComparatorType = AccessRowType & { name: string; lastName: string; firstName: string };

export type GroupSortingType = Pick<AccessRowType, 'id' | 'members'> & { name: string };
